/**
 * The typed event bus between the world (canvas) and UI (DOM) layers —
 * 106 §3. It is the ONLY channel between them: the world never reads the
 * DOM, the UI never touches canvas objects.
 */

export interface WorldToUiEvents {
  /** The single active interaction target changed (US 01.02). */
  'interaction-changed': { npcId: string | null };
  /** Player pressed INTERACT while a target was active (US 01.03). */
  'interact-requested': { npcId: string };
  /** A discrete UI intent drained by the world loop (US 12.01). */
  'ui-intent': {
    intent:
      'CANCEL' | 'OPEN_INVENTORY' | 'OPEN_JOURNAL' | 'OPEN_APARTMENT' | 'OPEN_ALBUM' | 'CONFIRM';
  };
  /** UI → world: mark an NPC's conversation paused/resumable (US 01.04). */
  'npc-paused': { npcId: string; paused: boolean };
  /** Connection tier changed (US 10.02). Tier 1 is deliberately invisible. */
  'connection-tier': { tier: 0 | 1 | 2 | 3 };
  /** World → transport glue: the player's current position, once per tick. */
  'player-position': { x: number; y: number };
  /** Fresh client-safe state after any sync — HUD, bag, markers feed on it. */
  'client-state': {
    money: number;
    health: number;
    deathDrop: { x: number; y: number; items: { itemId: string; qty: number }[] } | null;
    pendingNpcs: { npcId: string; count: number }[];
    hasBicycle: boolean;
    hasBoat: boolean;
  };
  /** Fast travel resolved server-side; the world teleports (US 01.11). */
  'travel-completed': { x: number; y: number };
  /** The player died (US 01.07): where the bag landed, what was lost. */
  'player-died': { x: number; y: number };
  /** World → UI: the player pressed INTERACT next to the dropped bag. */
  'bag-collect-requested': Record<string, never>;
  /** City-level info from sync — day counter, hazards, areas (US 03.x). */
  'city-info': {
    dayInfo: { day: number; daysUntilCutover: number | null };
    hazards: {
      id: string;
      path: { x: number; y: number }[];
      speedTilesPerSec: number;
      spawnIntervalMs: number;
      damage: number;
    }[];
    areas: { id: string; name?: string; open: boolean; blockerTile?: { x: number; y: number } }[];
    fishingSpots?: { id: string; x: number; y: number; deep: boolean }[];
  };
  /** World → UI: the player touched a hazard; UI reports it to the server. */
  'hazard-collision': { hazardId: string };
  /** Touch drag-stick state — drives the visual d-pad overlay (US 12.02). */
  'touch-state': { active: boolean; originX: number; originY: number; dx: number; dy: number };
  /** Last input device used — touch controls auto-hide on keyboard (US 12.02). */
  'input-device': { kind: 'touch' | 'keyboard' };
  /** UI → world: an on-screen control pressed a discrete intent (US 12.02). */
  'virtual-press': { intent: 'INTERACT' };
  /** Another tab claimed the game session — hard-freeze this one (US 15.05). */
  'session-locked': Record<string, never>;
  /** World → UI: the player pressed INTERACT at a fishing spot (US 20.01). */
  'fishing-requested': { spotId: string };
  /** UI → world: a modal minigame owns the input right now (US 20.01). */
  'modal-input': { captured: boolean };
  /** World → UI: a plain notice toast (e.g. boat access explainer). */
  notice: { text: string; danger: boolean };
}

type Handler<T> = (payload: T) => void;

export class EventBus<Events> {
  private handlers = new Map<keyof Events, Set<Handler<Events[keyof Events]>>>();

  on<K extends keyof Events>(event: K, handler: Handler<Events[K]>): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    const h = handler as Handler<Events[keyof Events]>;
    set.add(h);
    return () => set.delete(h);
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.handlers.get(event);
    if (!set) return;
    for (const handler of set) (handler as Handler<Events[K]>)(payload);
  }
}

/** The world→UI bus instance. UI→world traffic is the IntentHub. */
export const worldBus = new EventBus<WorldToUiEvents>();
